import * as Tone from 'tone';
import { clamp } from '../config/music';

/**
 * The permanent master chain. Sources connect to `input`; everything after it
 * is built once and never torn down (the effects section between `filter` and
 * `delay` is rewired by effects-chain.ts).
 *
 *   source → sourceTrim → filter → [enabled effects] → delay → reverb
 *          → polyTrim → master → limiter → softClip → destination
 */
export interface Graph {
  readonly sourceTrim: Tone.Gain;
  readonly filter: Tone.Filter;
  readonly delay: Tone.FeedbackDelay;
  readonly reverb: Tone.Reverb;
  readonly polyTrim: Tone.Gain;
  readonly master: Tone.Gain;
  readonly limiter: Tone.Limiter;
  readonly softClip: Tone.WaveShaper;
}

let graph: Graph | null = null;

/**
 * Input level at which the soft clipper reaches its asymptote. A WaveShaperNode
 * maps input [-1, 1] across its curve and hard-clamps anything beyond, so the
 * signal is scaled down into that window before shaping and the curve is
 * written for the wider range.
 */
const SOFT_CLIP_RANGE = 4;
/** Below this the transfer curve is exactly linear, so normal playing is untouched. */
const SOFT_CLIP_KNEE = 0.6;

/**
 * Transparent below the knee, asymptotically approaching ±1 above it. The
 * limiter alone could not do this job: it is a DynamicsCompressorNode with a
 * 3 ms attack, so transients sailed past it — an eight-note chord with the
 * volume knob up measured 3.28 at the destination, i.e. hard-clipped by the
 * sound card. This makes exceeding full scale arithmetically impossible.
 */
function softClipCurve(x: number): number {
  const a = Math.abs(x);
  if (a <= SOFT_CLIP_KNEE) return x;
  const over = (a - SOFT_CLIP_KNEE) / (1 - SOFT_CLIP_KNEE);
  const shaped = SOFT_CLIP_KNEE + (1 - SOFT_CLIP_KNEE) * Math.tanh(over);
  return Math.sign(x) * shaped;
}

/** Volume knob (0..1) → master gain. Capped below unity; the soft clipper is a net, not a tone control. */
export function volumeToGain(v: number): number {
  return Math.pow(clamp(v, 0, 1), 1.4) * 0.95;
}

export function ensureGraph(): Graph {
  if (graph) return graph;

  const sourceTrim = new Tone.Gain(1);
  const filter = new Tone.Filter({ type: 'lowpass', frequency: 5200, Q: 1 });
  const delay = new Tone.FeedbackDelay({ delayTime: 0.28, feedback: 0.35, wet: 0 });
  const reverb = new Tone.Reverb({ decay: 2.5, wet: 0.18 });
  const polyTrim = new Tone.Gain(1);
  const master = new Tone.Gain(volumeToGain(0.3));
  const limiter = new Tone.Limiter(-6);

  const preShaper = new Tone.Gain(1 / SOFT_CLIP_RANGE);
  const softClip = new Tone.WaveShaper((u: number) => softClipCurve(u * SOFT_CLIP_RANGE), 4096);

  sourceTrim.connect(filter);
  delay.connect(reverb);
  reverb.connect(polyTrim);
  polyTrim.connect(master);
  master.connect(limiter);
  limiter.connect(preShaper);
  preShaper.connect(softClip);
  softClip.toDestination();

  graph = { sourceTrim, filter, delay, reverb, polyTrim, master, limiter, softClip };
  return graph;
}

export const graphOrNull = (): Graph | null => graph;
export const isGraphReady = (): boolean => graph !== null;

const RAMP = 0.04;

export function setFilterFrequency(hz: number): void {
  graph?.filter.frequency.rampTo(hz, RAMP);
}
export function setEchoWet(wet: number): void {
  graph?.delay.wet.rampTo(clamp(wet, 0, 1), RAMP);
}
export function setReverbWet(wet: number): void {
  graph?.reverb.wet.rampTo(clamp(wet, 0, 1), RAMP);
}
export function setVolume(v: number): void {
  graph?.master.gain.rampTo(volumeToGain(v), 0.03);
}
export function setSourceTrim(gain: number): void {
  graph?.sourceTrim.gain.rampTo(gain, 0.03);
}

/**
 * Gentle headroom trim as the chord grows. Unlike the previous per-note 1/n
 * velocity scaling — which left the first two notes of a chord at full level
 * and dropped the third to half, and counted every sustained note — this is a
 * single ramped gain, so all sounding notes are affected identically.
 */
export function setActiveVoiceCount(count: number): void {
  if (!graph) return;
  const n = clamp(count, 1, 8);
  graph.polyTrim.gain.rampTo(1 / Math.sqrt(n), RAMP);
}

/**
 * Tone builds a few nodes (PluckSynth's comb filter, BitCrusher) as
 * AudioWorklets loaded from a blob: URL. A page opened straight off disk has an
 * opaque origin and Chrome refuses to load worklet modules there, failing with
 * "AbortError: Unable to load a worklet's module". Nothing in the page can work
 * around it, so affected features are flagged instead of failing silently.
 */
export const WORKLETS_BLOCKED =
  typeof AudioWorkletNode === 'undefined' || window.location.protocol === 'file:';

export const WORKLET_HINT =
  'Pagina e deschisă direct de pe disc (file://), unde browserul refuză modulele AudioWorklet: ' +
  'PluckSynth și BitCrusher nu pot funcționa. Rulează „npm run dev” și deschide adresa http ' +
  'afișată acolo ca să meargă.';
